import { useState, useEffect, memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSun,
  faMoon,
  faBars,
  faTimes,
  faHome,
  faComments,
  faVideo,
  faCog,
  faChartBar,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faBullseye,
  faBook,
  faUsers,
  faWrench,
  faClock,
  faExclamationTriangle,
  faChartLine,
  faGlobe,
  faGraduationCap,
  faArrowTrendUp,
  faTrafficLight,
  faCreditCard,
  faRoute,
  faCalendarAlt,
  faServer,
  faHexagonNodesBolt,
  faBolt,
  faLink,
  faFilter,
  faUpload,
  faCircleQuestion,
} from '@/fontawesome';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useUser } from '@/contexts/UserContext';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { useDayTradingLiveSession } from '@/hooks/useDayTradingLiveSession';
import { isEmptyFeatureConfig } from '@/models/feature-config';
import { canSeePricingPage } from '@/utils/subscription-status';
import UserAvatar from '@/components/ui/UserAvatar';
import { isImpersonating } from '@/services/impersonation';
import { DAY_TRADING_ROUTES, OPTION_SCREENER_ROUTES, JOURNAL_ROUTES } from '@/constants/routes';
import {faChartPie, faEnvelope, faUpDown, faCloud, faBell, faCalendarDay, faArrowRightFromBracket, faTag} from "@fortawesome/free-solid-svg-icons";
import {faDiscord, faTelegram} from "@fortawesome/free-brands-svg-icons";
import type {User} from "@/models";

interface SideNavigationProps {
  onLogout: () => void;
  onCollapsedChange?: (collapsed: boolean) => void;
}

function SideNavigation({ onLogout, onCollapsedChange }: SideNavigationProps) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Load collapsed state from localStorage on initial load
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const [isSignaleExpanded, setIsSignaleExpanded] = useState(() => {
    // Load expanded state from localStorage
    const saved = localStorage.getItem('signaleExpanded');
    return saved ? JSON.parse(saved) : false;
  });
  const [isAdminExpanded, setIsAdminExpanded] = useState(() => {
    // Load expanded state from localStorage
    const saved = localStorage.getItem('adminExpanded');
    return saved ? JSON.parse(saved) : false;
  });
  const [isSystemExpanded, setIsSystemExpanded] = useState(() => {
    // Load expanded state from localStorage
    const saved = localStorage.getItem('systemExpanded');
    return saved ? JSON.parse(saved) : false;
  });
  const [isJournalExpanded, setIsJournalExpanded] = useState(() => {
    // Load expanded state from localStorage
    const saved = localStorage.getItem('journalExpanded');
    return saved ? JSON.parse(saved) : false;
  });

  const toggleCollapsed = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsed));
    onCollapsedChange?.(newCollapsed);
  };

  const toggleSignale = () => {
    const newExpanded = !isSignaleExpanded;
    setIsSignaleExpanded(newExpanded);
    localStorage.setItem('signaleExpanded', JSON.stringify(newExpanded));
  };

  const toggleAdmin = () => {
    const newExpanded = !isAdminExpanded;
    setIsAdminExpanded(newExpanded);
    localStorage.setItem('adminExpanded', JSON.stringify(newExpanded));
  };

  const toggleSystem = () => {
    const newExpanded = !isSystemExpanded;
    setIsSystemExpanded(newExpanded);
    localStorage.setItem('systemExpanded', JSON.stringify(newExpanded));
  };

  const toggleJournal = () => {
    const newExpanded = !isJournalExpanded;
    setIsJournalExpanded(newExpanded);
    localStorage.setItem('journalExpanded', JSON.stringify(newExpanded));
  };

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { user, isAdmin, isStaff, loading: userLoading } = useUser();
  const { hasFeature } = useFeatureAccess();
  const { liveSessionId } = useDayTradingLiveSession();
  const hasNoFeatures = user !== null && !isStaff && isEmptyFeatureConfig(user.featureConfig);

  // Listen for window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Compute effective collapsed state - always false on mobile
  const effectiveIsCollapsed = isMobile ? false : isCollapsed;

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: faHome,
      current: location.pathname === '/dashboard'
    },
  ];

  const allNavigationAfterSignale = [
    {
      name: 'Markt-Analyse',
      href: '/market-analysis',
      icon: faChartLine,
      current: location.pathname === '/market-analysis',
      hasAccess: !hasNoFeatures,
      feature: null,
    },
    {
      name: 'Trading Journal',
      href: '/journal',
      icon: faBook,
      current: location.pathname.startsWith('/journal'),
      hasAccess: hasFeature('journal'),
      feature: 'journal' as const,
    },
    {
      name: 'Trading-Buddy',
      href: '/chats',
      icon: faComments,
      current: location.pathname === '/chats' || location.pathname.startsWith('/chats/'),
      hasAccess: hasFeature('chatbot'),
      feature: 'chatbot' as const
    },
    {
      name: 'Academy',
      href: '/videos',
      icon: faVideo,
      current: location.pathname === '/videos' || location.pathname.startsWith('/videos/'),
      hasAccess: !hasNoFeatures,
      feature: null
    },
    {
      name: 'Soziales',
      href: '/social',
      icon: faGlobe,
      current: location.pathname === '/social',
      hasAccess: !hasNoFeatures,
      feature: null
    },
    {
      // \u00A0 (geschütztes Leerzeichen): erzwingt den Zeilenumbruch nach "Dein"
      name: 'Dein nächster\u00A0Schritt',
      href: '/pricing',
      icon: faRoute,
      current: location.pathname === '/pricing',
      hasAccess: true,
      feature: null,
      // Community-Mitgliedern nur anzeigen, solange ihnen Boost oder Daytrading fehlt —
      // sonst gibt es auf /pricing nichts für sie (und die Seite leitet weiter). Erst nach
      // dem Laden des Users einblenden, sonst blitzt der CTA kurz auf. Wer alle Pakete hat,
      // bekommt auf /pricing die Ausbildung / 100K Community angeboten und behält den Eintrag.
      condition: (u: User|null) => !userLoading && u !== null && canSeePricingPage(u),
      isButton: true,
    },
  ];

  // Filter navigation items based on user permissions.
  // Trading-Buddy ist nur für User mit dem Chatbot-Feature sichtbar – alle anderen
  // sehen den Eintrag gar nicht (auch nicht als gesperrten Link).
  const navigationAfterSignale = allNavigationAfterSignale.filter(item => {
    if (item.name === 'Trading-Buddy' && !item.hasAccess) {
      return false;
    }
    return item.condition?.(user) ?? true;
  });

  // Signale navigation with subcategories
  const signaleNavigation = {
    name: 'Tradingideen',
    icon: faChartBar,
    current: location.pathname.startsWith('/signale'),
    subcategories: [
      {
        name: 'Kernstrategie',
        href: '/signale/kernstrategie',
        icon: faBullseye,
        current: location.pathname === '/signale/kernstrategie',
        hasAccess: hasFeature('coreRecommendations'),
        feature: 'coreRecommendations' as const
      },
      {
        name: 'Earnings Trades',
        href: '/signale/earning-trades',
        icon: faChartPie,
        current: location.pathname === '/signale/earning-trades',
        hasAccess: hasFeature('earningRecommendations'),
        feature: 'earningRecommendations' as const
      },
      {
        name: 'Swing Trades',
        href: '/signale/swing-trades',
        icon: faUpDown,
        current: location.pathname === '/signale/swing-trades',
        hasAccess: hasFeature('swingTradeRecommendations'),
        feature: 'swingTradeRecommendations' as const
      },
      {
        name: 'Langzeit Trades',
        href: '/signale/langzeit-trades',
        icon: faArrowTrendUp,
        current: location.pathname === '/signale/langzeit-trades',
        hasAccess: hasFeature('longTermRecommendations'),
        feature: 'longTermRecommendations' as const
      }
    ]
  };

  // Trading Journal navigation with subpages (rendered as an expandable group in place)
  const journalNavigation = {
    name: 'Trading Journal',
    icon: faBook,
    current: location.pathname.startsWith('/journal'),
    subcategories: [
      { name: 'Dashboard', href: JOURNAL_ROUTES.DASHBOARD, icon: faChartBar, current: location.pathname === JOURNAL_ROUTES.DASHBOARD },
      { name: 'Trades', href: JOURNAL_ROUTES.TRADES, icon: faChartLine, current: location.pathname.startsWith(JOURNAL_ROUTES.TRADES) },
      { name: 'Positionen', href: JOURNAL_ROUTES.POSITIONS, icon: faChartPie, current: location.pathname.startsWith(JOURNAL_ROUTES.POSITIONS) },
      { name: 'Kalender', href: JOURNAL_ROUTES.CALENDAR, icon: faCalendarDay, current: location.pathname.startsWith(JOURNAL_ROUTES.CALENDAR) },
      { name: 'Kapital', href: JOURNAL_ROUTES.CAPITAL_LADDER, icon: faArrowTrendUp, current: location.pathname.startsWith(JOURNAL_ROUTES.CAPITAL_LADDER) },
      { name: 'Zuteilungen', href: JOURNAL_ROUTES.ASSIGNMENTS, icon: faArrowRightFromBracket, current: location.pathname.startsWith(JOURNAL_ROUTES.ASSIGNMENTS) },
      { name: 'Reviews', href: JOURNAL_ROUTES.REVIEWS, icon: faBook, current: location.pathname.startsWith(JOURNAL_ROUTES.REVIEWS) },
      { name: 'Tags', href: JOURNAL_ROUTES.TAGS, icon: faTag, current: location.pathname.startsWith(JOURNAL_ROUTES.TAGS) },
      { name: 'Import', href: JOURNAL_ROUTES.IMPORT, icon: faUpload, current: location.pathname.startsWith(JOURNAL_ROUTES.IMPORT) },
      { name: 'Einstellungen', href: JOURNAL_ROUTES.SETTINGS, icon: faCog, current: location.pathname.startsWith(JOURNAL_ROUTES.SETTINGS) },
    ]
  };

  // Admin navigation with subcategories
  // adminOnly: true means only full admins can see this item (not employees)
  const allAdminSubcategories = [
    {
      name: 'Dashboard',
      href: '/admin',
      icon: faHome,
      current: location.pathname === '/admin',
      adminOnly: false
    },
    {
      name: 'Benutzer',
      href: '/admin/users',
      icon: faUsers,
      current: location.pathname.startsWith('/admin/users'),
      adminOnly: false
    },
    {
      name: 'Symbole',
      href: '/admin/symbols',
      icon: faChartLine,
      current: location.pathname.startsWith('/admin/symbols'),
      adminOnly: false
    },
    {
      name: 'Börsen',
      href: '/admin/stock-exchanges',
      icon: faGlobe,
      current: location.pathname.startsWith('/admin/stock-exchanges'),
      adminOnly: false
    },
    {
      name: 'Tradingideen',
      href: '/admin/trading-recommendations',
      icon: faBullseye,
      current: location.pathname.startsWith('/admin/trading-recommendations'),
      adminOnly: false
    },
    {
      name: 'Kurse',
      href: '/admin/courses',
      icon: faGraduationCap,
      current: location.pathname.startsWith('/admin/courses'),
      adminOnly: false
    },
    {
      name: 'Börsen-Ampel',
      href: '/admin/stocks-status-lights',
      icon: faTrafficLight,
      current: location.pathname.startsWith('/admin/stocks-status-lights'),
      adminOnly: false
    },
    {
      name: 'Events',
      href: '/admin/events',
      icon: faCalendarAlt,
      current: location.pathname.startsWith('/admin/events'),
      adminOnly: false
    },
    {
      name: 'Registrierungs-Links',
      href: '/admin/registrierungs-links',
      icon: faLink,
      current: location.pathname.startsWith('/admin/registrierungs-links'),
      adminOnly: true
    },
    {
      name: 'Fragen',
      href: '/admin/questions',
      icon: faCircleQuestion,
      current: location.pathname.startsWith('/admin/questions'),
      adminOnly: true,
    },
  ];

  // System navigation with subcategories (admin-only items)
  const allSystemSubcategories = [
    {
      name: 'Kurse',
      href: '/admin/all-courses',
      icon: faGraduationCap,
      current: location.pathname.startsWith('/admin/all-courses'),
    },
    {
      name: 'Module',
      href: '/admin/all-modules',
      icon: faBook,
      current: location.pathname.startsWith('/admin/all-modules'),
    },
    {
      name: 'Videos',
      href: '/admin/all-videos',
      icon: faVideo,
      current: location.pathname.startsWith('/admin/all-videos'),
    },
    {
      name: 'Abonnements',
      href: '/admin/subscriptions',
      icon: faCreditCard,
      current: location.pathname.startsWith('/admin/subscriptions'),
    },
    {
      name: 'Tools',
      href: '/admin/tools',
      icon: faWrench,
      current: location.pathname.startsWith('/admin/tools'),
    },
    {
      name: 'Jobs',
      href: '/admin/jobs',
      icon: faClock,
      current: location.pathname.startsWith('/admin/jobs'),
    },
    {
      name: 'Fehler',
      href: '/admin/errors',
      icon: faExclamationTriangle,
      current: location.pathname.startsWith('/admin/errors'),
    },
    {
      name: 'Postausgang',
      href: '/admin/mails',
      icon: faEnvelope,
      current: location.pathname.startsWith('/admin/mails'),
    },
    {
      name: 'Discord-Nachrichten',
      href: '/admin/discord-messages',
      icon: faDiscord,
      current: location.pathname.startsWith('/admin/discord-messages'),
    },
    {
      name: 'Telegram-Nachrichten',
      href: '/admin/telegram-messages',
      icon: faTelegram,
      current: location.pathname.startsWith('/admin/telegram-messages'),
    },
    {
      name: 'SCN-Nachrichten',
      href: '/admin/scn-messages',
      icon: faCloud,
      current: location.pathname.startsWith('/admin/scn-messages'),
    },
    {
      name: 'Push-Benachrichtigungen',
      href: '/admin/push-notifications',
      icon: faBell,
      current: location.pathname.startsWith('/admin/push-notifications'),
    },
    {
      name: 'Chat-Sitzungen',
      href: '/admin/sessions',
      icon: faComments,
      current: location.pathname.startsWith('/admin/sessions'),
    },
    {
      name: 'LLM-Tools',
      href: '/admin/llm-tools',
      icon: faHexagonNodesBolt,
      current: location.pathname.startsWith('/admin/llm-tools'),
    },
  ];

  // Filter subcategories based on permission level
  const adminSubcategories = allAdminSubcategories.filter(item => isAdmin || !item.adminOnly);

  const adminNavigation = {
    name: 'Admin',
    icon: faCog,
    current: location.pathname.startsWith('/admin') && !allSystemSubcategories.some(item => location.pathname.startsWith(item.href)),
    subcategories: adminSubcategories
  };

  const systemNavigation = {
    name: 'System',
    icon: faServer,
    current: allSystemSubcategories.some(item => location.pathname.startsWith(item.href)),
    subcategories: allSystemSubcategories
  };

  // Admin will be handled as expandable menu, no need to add to navigationAfterSignale

  return (
    <>
      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile menu button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className={`lg:hidden fixed left-4 z-50 inline-flex items-center justify-center p-2 rounded-lg text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors cursor-pointer ${isImpersonating() ? 'top-11' : 'top-4'}`}
      >
        <FontAwesomeIcon icon={isMobileMenuOpen ? faTimes : faBars} className="w-5 h-5" />
      </button>

      {/* Sidebar */}
      <div className={`
        fixed left-0 bottom-0 z-50 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-xl transition-all duration-300 ease-in-out flex flex-col overflow-hidden
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
        ${effectiveIsCollapsed ? 'w-16' : 'w-64'}
        ${isImpersonating() ? 'top-7' : 'top-0'}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
          <img
            src={isDarkMode ? "/logo_light.png" : "/logo.png"}
            alt="Börsencampus Logo"
            className={`h-9 object-contain transition-opacity duration-300 ${effectiveIsCollapsed ? 'opacity-0 w-0' : 'opacity-100'}`}
            loading="eager"
            fetchPriority="high"
          />
          <button
            onClick={toggleCollapsed}
            className="hidden lg:flex items-center justify-center p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
            title={isCollapsed ? 'Navigation ausklappen' : 'Navigation einklappen'}
          >
            <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronLeft} className="w-4 h-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {/* Dashboard */}
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={hasNoFeatures ? '/pricing' : item.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                hasNoFeatures
                  ? 'opacity-50 text-gray-500 dark:text-gray-500 hover:opacity-70'
                  : item.current
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title={effectiveIsCollapsed ? (hasNoFeatures ? `${item.name} (gesperrt)` : item.name) : undefined}
            >
              <FontAwesomeIcon icon={item.icon} className={`${effectiveIsCollapsed ? 'w-5 h-5' : 'w-5 h-5 mr-3'} flex-shrink-0`} />
              <span className={`truncate transition-all duration-300 ${effectiveIsCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                {item.name}
              </span>
            </Link>
          ))}

          {/* Signale with expandable subcategories - as second item */}
          <div className="space-y-1">
            <button
              data-tour="nav-signals"
              onClick={() => {
                // Always toggle subcategories, don't navigate
                toggleSignale();
              }}
              className={`group flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                hasNoFeatures
                  ? 'opacity-50 text-gray-500 dark:text-gray-500 hover:opacity-70'
                  : signaleNavigation.current
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title={effectiveIsCollapsed ? (hasNoFeatures ? `${signaleNavigation.name} (gesperrt)` : signaleNavigation.name) : undefined}
            >
              <div className="flex items-center">
                <FontAwesomeIcon icon={signaleNavigation.icon} className={`${effectiveIsCollapsed ? 'w-5 h-5' : 'w-5 h-5 mr-3'} flex-shrink-0`} />
                <span className={`truncate transition-all duration-300 ${effectiveIsCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                  {signaleNavigation.name}
                </span>
              </div>
              <FontAwesomeIcon
                icon={isSignaleExpanded ? faChevronUp : faChevronDown}
                className={`w-4 h-4 flex-shrink-0 transition-all duration-300 ${effectiveIsCollapsed ? 'opacity-0 w-0' : 'opacity-100'}`}
              />
            </button>

            {/* Subcategories - Show when expanded */}
            {isSignaleExpanded && (
              <div className={effectiveIsCollapsed ? 'space-y-1 mt-1' : 'ml-6 space-y-1'}>
                {signaleNavigation.subcategories.map((subItem) => (
                  <Link
                    key={subItem.name}
                    to={hasNoFeatures ? '/pricing' : subItem.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                      hasNoFeatures
                        ? 'opacity-50 text-gray-500 dark:text-gray-500 hover:opacity-70'
                        : subItem.current
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                    } ${
                      effectiveIsCollapsed ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''
                    }`}
                    title={effectiveIsCollapsed ? (hasNoFeatures ? `${subItem.name} (gesperrt)` : subItem.name) : undefined}
                  >
                    {effectiveIsCollapsed ? (
                      <FontAwesomeIcon icon={subItem.icon} className="w-5 h-5 flex-shrink-0 opacity-75" />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={subItem.icon} className="w-4 h-4 mr-3 flex-shrink-0" />
                        <span className="truncate">{subItem.name}</span>
                      </>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Daytrading */}
          <Link
              to={hasNoFeatures ? '/pricing' : DAY_TRADING_ROUTES.LIST}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer relative ${
                hasNoFeatures
                  ? 'opacity-50 text-gray-500 dark:text-gray-500 hover:opacity-70'
                  : location.pathname.startsWith('/daytrading')
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title={effectiveIsCollapsed ? (hasNoFeatures ? 'Daytrading (gesperrt)' : 'Daytrading') : undefined}
            >
              <FontAwesomeIcon icon={faBolt} className={`${effectiveIsCollapsed ? 'w-5 h-5' : 'w-5 h-5 mr-3'} flex-shrink-0`} />
              <span className={`truncate transition-all duration-300 ${effectiveIsCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                Daytrading
              </span>
              {liveSessionId !== null && (
                effectiveIsCollapsed ? (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                ) : (
                  <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 animate-pulse">
                    Live
                  </span>
                )
              )}
            </Link>

          {/* Option Screener - aktuell nur für Admins sichtbar (für normale User komplett ausgeblendet) */}
          {isAdmin && (
            <Link
              to={hasNoFeatures ? '/pricing' : OPTION_SCREENER_ROUTES.LIST}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer relative ${
                hasNoFeatures
                  ? 'opacity-50 text-gray-500 dark:text-gray-500 hover:opacity-70'
                  : location.pathname.startsWith('/option-screener')
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title={effectiveIsCollapsed ? (hasNoFeatures ? 'Option Screener (gesperrt)' : 'Option Screener') : undefined}
            >
              <FontAwesomeIcon icon={faFilter} className={`${effectiveIsCollapsed ? 'w-5 h-5' : 'w-5 h-5 mr-3'} flex-shrink-0`} />
              <span className={`truncate transition-all duration-300 ${effectiveIsCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                Option Screener
              </span>
            </Link>
          )}

          {/* Remaining navigation items after Signale */}
          {navigationAfterSignale.map((item) => {
            // Trading Journal renders as an expandable group (with subpages) when unlocked
            if (item.name === 'Trading Journal' && item.hasAccess) {
              return (
                <div key={item.name} className="space-y-1">
                  <button
                    onClick={() => toggleJournal()}
                    className={`group flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                      journalNavigation.current
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    title={effectiveIsCollapsed ? journalNavigation.name : undefined}
                  >
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={journalNavigation.icon} className={`${effectiveIsCollapsed ? 'w-5 h-5' : 'w-5 h-5 mr-3'} flex-shrink-0`} />
                      <span className={`truncate transition-all duration-300 ${effectiveIsCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                        {journalNavigation.name}
                      </span>
                    </div>
                    <FontAwesomeIcon
                      icon={isJournalExpanded ? faChevronUp : faChevronDown}
                      className={`w-4 h-4 flex-shrink-0 transition-all duration-300 ${effectiveIsCollapsed ? 'opacity-0 w-0' : 'opacity-100'}`}
                    />
                  </button>

                  {isJournalExpanded && (
                    <div className={effectiveIsCollapsed ? 'space-y-1 mt-1' : 'ml-6 space-y-1'}>
                      {journalNavigation.subcategories.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                            subItem.current
                              ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                              : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                          } ${
                            effectiveIsCollapsed ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''
                          }`}
                          title={effectiveIsCollapsed ? subItem.name : undefined}
                        >
                          {effectiveIsCollapsed ? (
                            <FontAwesomeIcon icon={subItem.icon} className="w-5 h-5 flex-shrink-0 opacity-75" />
                          ) : (
                            <>
                              <FontAwesomeIcon icon={subItem.icon} className="w-4 h-4 mr-3 flex-shrink-0" />
                              <span className="truncate">{subItem.name}</span>
                            </>
                          )}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={item.name}
                data-tour={
                  item.name === 'Markt-Analyse' ? 'nav-market-analysis' :
                  item.name === 'Academy' ? 'nav-academy' :
                  undefined
                }
                to={item.hasAccess ? item.href : (item.feature !== null ? `/feature-locked/${item.feature}` : '/pricing')}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                  'isButton' in item && item.isButton
                    ? 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600'
                    : !item.hasAccess
                    ? 'opacity-50 text-gray-500 dark:text-gray-500 hover:opacity-70'
                    : item.current
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                title={effectiveIsCollapsed ? (item.hasAccess ? item.name : `${item.name} (gesperrt)`) : undefined}
              >
                <FontAwesomeIcon icon={item.icon} className={`${effectiveIsCollapsed ? 'w-5 h-5' : 'w-5 h-5 mr-3'} flex-shrink-0`} />
                {/* Button-Einträge (CTA) dürfen zweizeilig umbrechen, normale Einträge werden abgeschnitten */}
                <span
                  className={`${'isButton' in item && item.isButton ? 'whitespace-normal leading-snug' : 'truncate'} transition-all duration-300 ${effectiveIsCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}
                >
                  {item.name}
                </span>
              </Link>
            );
          })}

          {/* Admin with expandable subcategories */}
          {isStaff && (
            <div className="space-y-1">
              <button
                onClick={() => {
                  // Always toggle subcategories, don't navigate
                  toggleAdmin();
                }}
                className={`group flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                  adminNavigation.current
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                title={effectiveIsCollapsed ? adminNavigation.name : undefined}
              >
                <div className="flex items-center">
                  <FontAwesomeIcon icon={adminNavigation.icon} className={`${effectiveIsCollapsed ? 'w-5 h-5' : 'w-5 h-5 mr-3'} flex-shrink-0`} />
                  <span className={`truncate transition-all duration-300 ${effectiveIsCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                    {adminNavigation.name}
                  </span>
                </div>
                <FontAwesomeIcon
                  icon={isAdminExpanded ? faChevronUp : faChevronDown}
                  className={`w-4 h-4 flex-shrink-0 transition-all duration-300 ${effectiveIsCollapsed ? 'opacity-0 w-0' : 'opacity-100'}`}
                />
              </button>

              {/* Admin Subcategories - Show when expanded */}
              {isAdminExpanded && (
                <div className={effectiveIsCollapsed ? 'space-y-1 mt-1' : 'ml-6 space-y-1'}>
                  {adminNavigation.subcategories.map((subItem) => (
                    <Link
                      key={subItem.name}
                      to={subItem.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        subItem.current
                          ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                      } ${
                        effectiveIsCollapsed ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''
                      }`}
                      title={effectiveIsCollapsed ? subItem.name : undefined}
                    >
                      {effectiveIsCollapsed ? (
                        <FontAwesomeIcon icon={subItem.icon} className="w-5 h-5 flex-shrink-0 opacity-75" />
                      ) : (
                        <>
                          <FontAwesomeIcon icon={subItem.icon} className="w-4 h-4 mr-3 flex-shrink-0" />
                          <span className="truncate">{subItem.name}</span>
                        </>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* System with expandable subcategories (admin-only) */}
          {isAdmin && (
            <div className="space-y-1">
              <button
                onClick={() => {
                  toggleSystem();
                }}
                className={`group flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                  systemNavigation.current
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                title={effectiveIsCollapsed ? systemNavigation.name : undefined}
              >
                <div className="flex items-center">
                  <FontAwesomeIcon icon={systemNavigation.icon} className={`${effectiveIsCollapsed ? 'w-5 h-5' : 'w-5 h-5 mr-3'} flex-shrink-0`} />
                  <span className={`truncate transition-all duration-300 ${effectiveIsCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                    {systemNavigation.name}
                  </span>
                </div>
                <FontAwesomeIcon
                  icon={isSystemExpanded ? faChevronUp : faChevronDown}
                  className={`w-4 h-4 flex-shrink-0 transition-all duration-300 ${effectiveIsCollapsed ? 'opacity-0 w-0' : 'opacity-100'}`}
                />
              </button>

              {/* System Subcategories - Show when expanded */}
              {isSystemExpanded && (
                <div className={effectiveIsCollapsed ? 'space-y-1 mt-1' : 'ml-6 space-y-1'}>
                  {systemNavigation.subcategories.map((subItem) => (
                    <Link
                      key={subItem.name}
                      to={subItem.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        subItem.current
                          ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                      } ${
                        effectiveIsCollapsed ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''
                      }`}
                      title={effectiveIsCollapsed ? subItem.name : undefined}
                    >
                      {effectiveIsCollapsed ? (
                        <FontAwesomeIcon icon={subItem.icon} className="w-5 h-5 flex-shrink-0 opacity-75" />
                      ) : (
                        <>
                          <FontAwesomeIcon icon={subItem.icon} className="w-4 h-4 mr-3 flex-shrink-0" />
                          <span className="truncate">{subItem.name}</span>
                        </>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Bottom section - fixed at bottom */}
        <div className="mt-auto">
          {/* User controls */}
          <div className="p-3 space-y-2 border-t border-gray-200 dark:border-gray-700">
            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              className={`w-full inline-flex items-center ${effectiveIsCollapsed ? 'justify-center' : ''} px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors cursor-pointer`}
              title={effectiveIsCollapsed ? (isDarkMode ? 'Light Mode aktivieren' : 'Dark Mode aktivieren') : undefined}
            >
              <FontAwesomeIcon icon={isDarkMode ? faSun : faMoon} className="w-5 h-5 flex-shrink-0" />
              <span className={`ml-3 whitespace-nowrap transition-all duration-300 ${effectiveIsCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </span>
            </button>

            {/* User Avatar */}
            <div className={effectiveIsCollapsed ? 'flex justify-center' : ''}>
              <UserAvatar onLogout={onLogout} collapsed={effectiveIsCollapsed} />
            </div>
          </div>
        </div>
      </div>

    </>
  );
}

export default memo(SideNavigation);
